import { lazy, type ReactNode } from 'react';
import { redirect, useLocation, type RouteObject } from 'react-router';

import { PATHS } from '../../../AppDomains';
import { LogoDestination } from '../../../assets/Logo/Logo';
import { NotFound } from '../../../components/ErrorPage/NotFound/NotFound';
import { getOrCreateFeatureFlag } from '../../../config/FeatureFlag';
import {
	getAllowedPopulationHealthManagementPages,
	getPopulationHealthManagementPagePath,
	POPULATION_HEALTH_MANAGEMENT_PAGES,
	POPULATION_HEALTH_MANAGEMENT_TABS_FLAG_NAME,
	PopulationHealthManagementPage,
	type PopulationHealthManagementFeatureFlagId,
} from '../../../pages/PopulationHealthManagementPage/PopulationHealthManagementPagesStaticData';
import { MainLayout, type LayoutHeaderProps } from '../../layouts/MainLayout/MainLayout';
import { ApplicationsMenuSelectedPage } from '../../layouts/MainLayout/MainLayoutHeader';
import { withLayout } from '../../layouts/withLayout';

const LazyPopulationHealthManagementLayout = lazy(() =>
	import('../../../components/PopulationHealthManagement/PopulationHealthManagementLayout').then((module) => ({
		default: module.PopulationHealthManagementLayout,
	})),
);

const LazyPopulationHealthManagementPageGuard = lazy(() =>
	import('../../../pages/PopulationHealthManagementPage/PopulationHealthManagementPageGuard').then((module) => ({
		default: module.PopulationHealthManagementPageGuard,
	})),
);

const LazyPopulationHealthHomePage = lazy(() =>
	import('../../../pages/PopulationHealthManagementPage/PopulationHealthHomePage').then((module) => ({
		default: module.PopulationHealthHomePage,
	})),
);

const LazySchedulingPatientsPage = lazy(() =>
	import('../../../pages/PopulationHealthManagementPage/SchedulingPatientsPage').then((module) => ({
		default: module.SchedulingPatientsPage,
	})),
);

const LazyPatientsWorkListPage = lazy(() =>
	import('../../../pages/PopulationHealthManagementPage/PatientsWorkListPage').then((module) => ({
		default: module.PatientsWorkListPage,
	})),
);

const LazyCareGapWorkspacePage = lazy(() =>
	import('../../../pages/PopulationHealthManagementPage/CareGapWorkspacePage').then((module) => ({
		default: module.CareGapWorkspacePage,
	})),
);

const LazyPopulationHealthArchivePage = lazy(() =>
	import('../../../pages/PopulationHealthManagementPage/PopulationHealthArchivePage').then((module) => ({
		default: module.PopulationHealthArchivePage,
	})),
);

const POPULATION_HEALTH_MANAGEMENT_PAGE_ELEMENT_BY_ID = {
	[PopulationHealthManagementPage.Home]: <LazyPopulationHealthHomePage />,
	[PopulationHealthManagementPage.SchedulingPatients]: <LazySchedulingPatientsPage />,
	[PopulationHealthManagementPage.PatientsWorkList]: <LazyPatientsWorkListPage />,
	[PopulationHealthManagementPage.CareGapWorkspace]: <LazyCareGapWorkspacePage />,
	[PopulationHealthManagementPage.Archive]: <LazyPopulationHealthArchivePage />,
} as const satisfies Record<PopulationHealthManagementPage, ReactNode>;

const POPULATION_HEALTH_MANAGEMENT_MAIN_LAYOUT_PROPS = {
	headerAppMenuSelectedPage: ApplicationsMenuSelectedPage.Quality,
	headerTitle: 'Population Health Management',
	headerLogoLink: LogoDestination.Nowhere,
} as const satisfies LayoutHeaderProps;

function PopulationHealthManagementNotFound() {
	const { pathname } = useLocation();
	const lastSegment = pathname.split('/').findLast(Boolean);

	return <NotFound {...{ entityName: `'${lastSegment}' page` }} />;
}

const POPULATION_HEALTH_MANAGEMENT_AUTO_SELECT_ROUTE = {
	index: true,
	loader: async function autoSelectPopulationHealthManagementPageLoader() {
		const { whenFlagsInitialized, getFeatureValue } = getOrCreateFeatureFlag();

		await whenFlagsInitialized();

		const allowedFeatureFlagNames = getFeatureValue<ReadonlyArray<PopulationHealthManagementFeatureFlagId>>(
			POPULATION_HEALTH_MANAGEMENT_TABS_FLAG_NAME,
			[] as const,
		);

		const [firstAllowedPage] = getAllowedPopulationHealthManagementPages(allowedFeatureFlagNames);

		if (!firstAllowedPage) {
			throw new Error('No allowed Population Health Management pages configured for the user');
		}

		return redirect(getPopulationHealthManagementPagePath(firstAllowedPage.path));
	},
} as const satisfies RouteObject;

const POPULATION_HEALTH_MANAGEMENT_PAGE_ROUTES = [
	POPULATION_HEALTH_MANAGEMENT_AUTO_SELECT_ROUTE,
	...POPULATION_HEALTH_MANAGEMENT_PAGES.map(function toPageRoute({ id, path }): RouteObject {
		return {
			path,
			element: <LazyPopulationHealthManagementPageGuard {...{ page: id }} />,
			children: [
				{
					index: true,
					element: POPULATION_HEALTH_MANAGEMENT_PAGE_ELEMENT_BY_ID[id],
				},
			],
		};
	}),
	{
		path: '*',
		element: <PopulationHealthManagementNotFound />,
	},
] as const satisfies ReadonlyArray<RouteObject>;

export const POPULATION_HEALTH_MANAGEMENT_ROUTES = [
	{
		...withLayout(<MainLayout {...POPULATION_HEALTH_MANAGEMENT_MAIN_LAYOUT_PROPS} />, [
			{
				path: PATHS.POPULATION_HEALTH_MANAGEMENT_PATH,
				element: <LazyPopulationHealthManagementLayout />,
				children: [...POPULATION_HEALTH_MANAGEMENT_PAGE_ROUTES],
			},
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;
